import {
  Business as BusinessIcon,
  Description as DescriptionIcon,
  ExpandLess,
  ExpandMore,
  People as PeopleIcon,
  Settings
} from '@mui/icons-material';
import {
  Avatar,
  Box,
  Collapse,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import { FiSidebar } from 'react-icons/fi';
import { Outlet } from 'react-router-dom';
import { useTabManagement } from './TabContext';

interface NavItemProps {
  icon: React.ReactElement;
  text: string;
  active: boolean;
  onClick: () => void;
  theme: any;
  sx?: any;
}

const NavItem: React.FC<NavItemProps> = ({ icon, text, active, onClick, theme, sx }) => (
  <motion.div
    whileHover={{ scale: 1.02 }}
    whileTap={{ scale: 0.98 }}
    transition={{ type: 'spring', stiffness: 400, damping: 17 }}
  >
    <ListItem
      button
      selected={active}
      onClick={onClick}
      sx={{
        borderRadius: '8px',
        mb: '4px',
        px: '12px',
        ...sx,
        '&.Mui-selected': {
          backgroundColor: 'rgba(25, 118, 210, 0.08)',
          '& .MuiListItemText-primary': {
            fontWeight: '600'
          }
        },
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.04)'
        }
      }}
    >
      <ListItemIcon sx={{ minWidth: '40px' }}>
        {React.cloneElement(icon as React.ReactElement<any>, {
          sx: {
            fontSize: '20px',
            color: active ? theme.palette.primary.main : theme.palette.text.secondary
          }
        })}
      </ListItemIcon>
      <ListItemText
        primary={text}
        primaryTypographyProps={{
          fontSize: '0.875rem',
          color: active ? theme.palette.primary.main : theme.palette.text.primary
        }}
      />
    </ListItem>
  </motion.div>
);

const PayroleLandingPage: React.FC = () => {
  const { activeTab, setActiveTab } = useTabManagement();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [navOpen, setNavOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(true);

  useEffect(() => {
    setNavOpen(false);
  }, [isMobile]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const toggleNav = () => setNavOpen(!navOpen);
  const toggleAdmin = () => setAdminOpen(!adminOpen);

  // Animation variants for right-side drawer
  const drawerVariants = {
    open: { x: 0, opacity: 1 },
    closed: { x: '100%', opacity: 0 }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        backgroundColor: theme.palette.grey[50]
      }}
    >
      {/* Main Content Area */}
      <Box>
        {/* Main Content */}
        <Box component="main">
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Paper
              elevation={0}
              sx={{
                p: { xs: '5px', sm: '10px' },
                borderRadius: '12px',
                minHeight: 'calc(100vh - 118px)',
                border: `1px solid ${theme.palette.divider}`,
                backgroundColor: theme.palette.background.paper
              }}
            >
              <Outlet />
            </Paper>
          </motion.div>
        </Box>
      </Box>

      {/* <Box
        sx={{
          position: 'fixed',
          top: '80px',
          right: isMobile ? '16px' : '24px',
          zIndex: theme.zIndex.drawer + 4,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          backgroundColor: '#ffffff',
          padding: '8px 12px',
          borderRadius: '32px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
          border: '2px solid #d0d7de',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: '0 6px 24px rgba(0, 0, 0, 0.2)'
          }
        }}
        onClick={toggleNav}
      >
        <FiSidebar
          size={20}
          style={{
            transform: navOpen ? 'rotate(90deg)' : 'rotate(0deg)',
            transition: 'transform 0.3s ease',
            color: '#344054'
          }}
        />
        <Typography variant="subtitle2" fontWeight="500" color="#344054">
          Payroll Options
        </Typography>
      </Box>

      <AnimatePresence>
        {navOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.5 }}
              exit={{ opacity: 0 }}
              onClick={toggleNav}
              style={{
                position: 'fixed',
                top: '70px',
                left: '95px',
                right: 0,
                bottom: 0,
                backgroundColor: '#000',
                zIndex: theme.zIndex.drawer + 2
              }}
            />

            <motion.div
              initial="closed"
              animate="open"
              exit="closed"
              variants={drawerVariants}
              transition={{ type: 'spring', damping: 25 }}
              style={{
                position: 'fixed',
                top: '70px',
                right: 0,
                width: isMobile ? '100%' : '300px',
                height: isMobile ? '80vh' : 'calc(100vh - 70px)',
                backgroundColor: theme.palette.background.paper,
                borderLeft: `1px solid ${theme.palette.divider}`,
                zIndex: theme.zIndex.drawer + 3,
                overflowY: 'auto',
                boxShadow: '-4px 0 10px rgba(0,0,0,0.1)'
              }}
            >
              <Box sx={{ p: '16px', minHeight: '100px' }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mb: '16px',
                    px: '8px'
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: theme.palette.primary.main,
                      width: '40px',
                      height: '40px',
                      mr: '12px'
                    }}
                  >
                    P
                  </Avatar>
                  <Typography variant="subtitle1" fontWeight="600">
                    Payroll
                  </Typography>
                </Box>

                <List sx={{ p: 0 }}>
                  <NavItem
                    icon={<PeopleIcon />}
                    text="People"
                    active={activeTab === 'people'}
                    onClick={() => {
                      setActiveTab('people');
                      toggleNav();
                    }}
                    theme={theme}
                  />

                  <NavItem
                    icon={<DescriptionIcon />}
                    text="Documents"
                    active={activeTab === 'documents'}
                    onClick={() => {
                      setActiveTab('documents');
                      toggleNav();
                    }}
                    theme={theme}
                  />

                  <NavItem
                    icon={<BusinessIcon />}
                    text="Organisation"
                    active={activeTab === 'clients-organisation'}
                    onClick={() => {
                      // Pass navigation state indicating source
                      setActiveTab('clients-organisation', {
                        isFromDocument: true,
                        previousTab: activeTab
                      });
                      toggleNav();
                    }}
                    theme={theme}
                  />

                  <ListItem
                    button
                    onClick={toggleAdmin}
                    sx={{
                      borderRadius: '8px',
                      px: '12px',
                      mb: '4px'
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: '40px' }}>
                      <Settings
                        sx={{
                          fontSize: '20px',
                          color: theme.palette.text.secondary
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary="Administration"
                      primaryTypographyProps={{
                        fontSize: '0.875rem',
                        color: theme.palette.text.primary
                      }}
                    />
                    {adminOpen ? (
                      <ExpandLess sx={{ color: theme.palette.text.secondary }} />
                    ) : (
                      <ExpandMore sx={{ color: theme.palette.text.secondary }} />
                    )}
                  </ListItem>

                  <Collapse in={adminOpen} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      <NavItem
                        icon={<span style={{ width: '24px' }} />}
                        text="Onboarding"
                        active={activeTab === 'onboarding'}
                        onClick={() => {
                          setActiveTab('onboarding');
                          toggleNav();
                        }}
                        theme={theme}
                        sx={{ pl: 4 }}
                      />
                    </List>
                  </Collapse>
                </List>
              </Box>
            </motion.div>
          </>
        )}
      </AnimatePresence> */}
    </Box>
  );
};

export default PayroleLandingPage;
